import { Link, Star, Minus, Plus } from "lucide-react";
import { eur } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const StarRating = ({ value = 0, count, size = 13, onRate, className = "" }) => {
  const { t } = useLang();
  return (
  <span className={`inline-flex items-center gap-1.5 ${className}`}>
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!onRate}
          onClick={() => onRate && onRate(i)}
          aria-label={t("misc.starAria").replace("{n}", i)}
          className={onRate ? "cursor-pointer" : "cursor-default"}
        >
          <Star
            size={size}
            className={i <= Math.round(value) ? "fill-gold text-gold" : "text-smoke/60"}
          />
        </button>
      ))}
    </span>
    {count !== undefined && <span className="font-mono text-[10px] text-smoke">({count})</span>}
  </span>
  );
};

export const StockBadge = ({ stock }) => {
  const { t } = useLang();
  if (stock <= 0) {
    return <span data-testid="stock-badge" className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400/90 border border-red-400/30 px-3 py-1">{t("stock.out")}</span>;
  }
  if (stock <= 5) {
    return <span data-testid="stock-badge" className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-300 border border-amber-300/40 px-3 py-1">{t("stock.last").replace("{n}", stock)}</span>;
  }
  return <span data-testid="stock-badge" className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300/90 border border-emerald-300/30 px-3 py-1">{t("stock.in")}</span>;
};

export const PriceTag = ({ price, discountPrice, className = "" }) => {
  if (!price || price <= 0) {
    return (
      <span data-testid="price-hidden" className={`font-serif text-xl text-gold/70 ${className}`}>
        —
      </span>
    );
  }
  const hasDiscount = discountPrice && discountPrice < price;
  return (
    <span className={`flex items-baseline gap-3 ${className}`}>
      <span className={`font-serif ${hasDiscount ? "text-2xl text-gold" : "text-xl text-cream"}`}>
        {eur(hasDiscount ? discountPrice : price)}
      </span>
      {hasDiscount && <span className="text-sm text-smoke line-through">{eur(price)}</span>}
      {hasDiscount && (
        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-obsidian bg-gold px-2 py-0.5">
          −{Math.round((1 - discountPrice / price) * 100)}%
        </span>
      )}
    </span>
  );
};

export const QtyStepper = ({ value, onChange, max = 99, testid = "qty" }) => {
  const { t } = useLang();
  return (
  <span className="inline-flex items-center border border-cream/15">
    <button data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(1, value - 1))} className="w-9 h-9 flex items-center justify-center text-mist hover:text-gold transition-colors" aria-label={t("misc.less")}>
      <Minus size={13} />
    </button>
    <span data-testid={`${testid}-value`} className="w-10 text-center font-mono text-sm">{value}</span>
    <button data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} className="w-9 h-9 flex items-center justify-center text-mist hover:text-gold disabled:opacity-30 transition-colors" aria-label={t("misc.more")}>
      <Plus size={13} />
    </button>
  </span>
  );
};

export const PageHeader = ({ eyebrow, title, sub }) => (
  <div className="mb-10">
    {eyebrow && <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold/85 mb-4">{eyebrow}</p>}
    <h1 className="font-serif font-light uppercase text-cream text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">{title}</h1>
    {sub && <p className="mt-4 max-w-2xl text-sm text-mist leading-relaxed">{sub}</p>}
  </div>
);
